import { Backdrop, Fade, IconButton, Modal } from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, {
    Ref,
    forwardRef,
    useContext,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from "react";

import { UserAppContext } from "contexts";
import get from "lodash/get";
import {
    useCreateAnnualSubscriptionWithDiscountMutation,
    useRetriveStripeCustomerQuery,
    useSharedQuery
} from "services/webServer";
import MembershipOfferInvalidPaymentModal, {
    IMembershipOfferInvalidPaymentModalRef,
} from "../MembershipOfferInvalidPaymentModal/MembershipOfferInvalidPaymentModal";
import MembershipOfferClaimedModal, {
    IMembershipOfferClaimedModalRef,
} from "../MembershipOfferJulyClaimedModal/MembershipOfferClaimedModal";

import { useHideExternalIcon } from "Hooks/useHideExternalIcon";
import Swal from "sweetalert2";

import { useHistory } from "react-router-dom";
import { useGetSubscriptionData } from "Screens/Memberships/Detail/ReviewAndCancel/ReviewAndCancel.hooks";
import { MEMBERSHIP_OFFER_KEY } from "../MembershipsMain.constant";
import styles from "./MembershipOfferModal.module.scss";
import { Close } from "@mui/icons-material";

const useStyles = makeStyles({
    modal: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",

        "@media (max-width: 768px)": {
            alignItems: "flex-start",
            marginTop: "20px",
        },
    },
});

interface IMembershipOfferModalProps {
    membershipId?: string;
}

export interface IMembershipOfferModalRef {
    setIsVisible: (visible: boolean) => void;
    isVisible: boolean;
}

export interface IMembershipOfferModal2025Ref {
    setIsVisible: (visible: boolean) => void;
    isVisible: boolean;
}

const MembershipOffer2025Modal = (
    {
        membershipId = ""
    }: IMembershipOfferModalProps,
    ref: Ref<IMembershipOfferModalRef>
) => {
    const [isVisible, setIsVisible] = useState<boolean>(false);
    useHideExternalIcon(isVisible);

    const classes = useStyles();
    const { auth } = useContext(UserAppContext);
    const { data } = useSharedQuery();
    const history = useHistory();
    const [reactiveId, setReactiveId] = useState<string | undefined>(undefined);

    const { subscriptionData, isLoadingSubscription } = useGetSubscriptionData(membershipId)

    const [createAnnualSubscriptionWithDiscount] =
        useCreateAnnualSubscriptionWithDiscountMutation();

    const membershipInvalidPaymentRef =
        useRef<IMembershipOfferInvalidPaymentModalRef>(null);

    const membershipClaimedRef = useRef<IMembershipOfferClaimedModalRef>(null);

    const stripeCustomerId = get(
        data,
        "currentUser.profile.stripe.customer_id"
    );

    const { data: customerData } = useRetriveStripeCustomerQuery({
        variables: {
            customerId: stripeCustomerId,
            params: {
                "expand[]": "invoice_settings.default_payment_method",
            },
        },
        fetchPolicy: "no-cache",
        skip: Boolean(!stripeCustomerId),
    });


    const cardInfo = useMemo(() => {
        if (!customerData || !customerData?.retriveStripeCustomer)
            return undefined;

        const defaultPaymentInfo =
            customerData?.retriveStripeCustomer?.invoice_settings
                ?.default_payment_method || undefined;

        if (defaultPaymentInfo) {
            return {
                exp_month: defaultPaymentInfo?.card?.exp_month,
                exp_year: defaultPaymentInfo?.card?.exp_year,
            };
        }
        return undefined;
    }, [customerData]);

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const handleClose = () => {
        setIsVisible(false);
    };

    const handleCheckCardExpired = ({
        expMonth,
        expYear,
    }: {
        expMonth: number;
        expYear: number;
    }) => {
        if (!expMonth || !expYear) return true;

        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1;
        const currentYear = currentDate.getFullYear();

        return (
            expYear < currentYear ||
            (expYear === currentYear && expMonth < currentMonth)
        );
    };

    const currency = useMemo(() => {
        return subscriptionData?.avironSubscription?.plan?.currency?.toUpperCase() || "USD"
    }, [subscriptionData])

    const handleClickRedeemOffer = async () => {
        const isExpired = handleCheckCardExpired({
            expMonth: cardInfo?.exp_month,
            expYear: cardInfo?.exp_year,
        });
        if (isExpired) {
            setIsVisible(false);
            membershipInvalidPaymentRef?.current?.setIsVisible(true);
            return;
        }

        if (!auth?.email || Boolean(isSubmitting)) return;
        try {
            setIsSubmitting(true);
            const { data } = await createAnnualSubscriptionWithDiscount({
                variables: {
                    email: auth.email,
                    currency,
                },
            });

            setIsVisible(false);
            setIsSubmitting(false);
            setReactiveId(data?.createAnnualSubscriptionWithDiscount?.subscription?.id);
            membershipClaimedRef?.current?.setIsVisible(true);
        } catch (error) {
            setIsVisible(false);

            const isCardDeclined =
                error.graphQLErrors?.[0]?.key === "stripe_card_declined";
            if (isCardDeclined) {
                await Swal.fire(
                    "Oops",
                    `
                Your card was declined. Please update your payment methods and try again
                `,
                    "error"
                );
                history.push(`/payment_method`);
            } else {
                await Swal.fire(
                    "Oops",
                    `
                It seem like you’re not eligible for
                this offer.
                `,
                    "error"
                );
                localStorage.removeItem(MEMBERSHIP_OFFER_KEY);
                setIsSubmitting(false);
                window.location.reload();
            }
        }
    };

    useImperativeHandle(
        ref,
        () => ({
            isVisible,
            setIsVisible,
        }),
        [isVisible]
    );

    return (
        <>
            <Modal
                open={isVisible}
                onClose={handleClose}
                className={classes.modal}
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={isVisible}>
                    <div className={styles.mainContainer}>
                        <div
                            className={styles["membership_offer_modal-header"]}
                        >
                            <IconButton
                                onClick={handleClose}
                                className="close-button"
                                size="medium"
                            >
                                <Close/>
                            </IconButton>
                        </div>

                        <div className={styles.logo} />
                        <div className={styles.flexRow}>
                            <span className={styles.specialOffer}>Special July<br /> 4th offer</span>
                            <div className={styles.vector} />
                            <div className={styles.vector1} />
                        </div>
                        <div className={styles.contentWarrapper}>
                            <div className={styles.welcomeBackRowaviron}>
                                <span className={styles.welcomeBack}>WELCOME BACK{data?.currentUser?.firstName && <>,</>} </span>
                                {data?.currentUser?.firstName &&
                                    <span className={styles.row4aviron}>
                                        {data?.currentUser?.firstName || ""}
                                    </span>}
                            </div>
                            <div className={styles.frame}>
                                <div className={styles.frame2}>
                                    <span className={styles.getOffAnnual}>
                                        GET 50% OFF AN ANNUAL MEMBERSHIP
                                    </span>
                                </div>
                            </div>
                            <div className={styles.flexRow3}>
                                <div className={styles.frame4}>
                                    <span className={styles.getOffAnnualRejoin}>
                                        Get 50% off an annual membership when you rejoin today*. This offer
                                        ends July 11.
                                    </span>
                                </div>

                                <div
                                    className={styles["membership_offer-data"]}
                                >
                                    <div>
                                        <p className={styles["data-tag"]}>
                                            OFFER EXPIRES SOON
                                        </p>
                                        <p className={styles["data-title"]}>
                                            ANNUAL FAMILY MEMBERSHIP
                                            {/* {lastestSubscription?.name} */}
                                        </p>
                                        <p
                                            className={
                                                styles["data-description"]
                                            }
                                        >
                                            {currency === "USD" ? "$204/year" : "$264/year"}, billed annually
                                        </p>
                                    </div>
                                    <div className={styles["price"]}>
                                        <p className={styles["price_original"]}>
                                            {currency === "USD" ? "$34" : "$44"}
                                        </p>

                                        <p className={styles["price_discount"]}>
                                            {currency === "USD" ? "$17" : "$22"}/
                                            <span className={styles["unit"]}>
                                                MONTH
                                            </span>
                                        </p>
                                    </div>
                                    <div className={styles["currency"]}>
                                        {currency}
                                        <img
                                            src="/images/icon-down.png"
                                            alt="Aviron Membership Offer"
                                            width={8}
                                            height={4}
                                            style={{
                                                marginLeft: "8px",
                                                marginBottom: "3px",
                                            }}
                                        />
                                    </div>
                                </div>

                            </div>
                            <div className={styles.buttonBig} onClick={handleClickRedeemOffer}>
                                <span className={styles.nextStep}>
                                    {isSubmitting
                                        ? "REDEEMING..."
                                        : "REDEEM OFFER"}
                                </span>
                            </div>
                            <span className={styles.termsAndConditions}>
                                *Terms and conditions apply.
                            </span>
                        </div>
                        <div className={styles.group} />
                    </div>
                </Fade>
            </Modal>
            <MembershipOfferInvalidPaymentModal
                ref={membershipInvalidPaymentRef}
            />
            <MembershipOfferClaimedModal
                ref={membershipClaimedRef}
                id={reactiveId}
            />
        </>
    );
};

export default forwardRef(MembershipOffer2025Modal);
